import { HeardSeekButton } from '@components/ui/heard-seek-button';
import { Input } from '@components/ui/input';
import { MultiSelect } from '@components/ui/multi-select';
import { ReasoningTooltip, ReviewButton } from '@components/ui/review-button';
import { SearchableSelect } from '@components/ui/searchable-select';
import { useAsset } from '@hooks/useAsset';
import { Check, Edit2, Sparkles, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import type { FieldEvidence, HeardSeek } from '@/components/fields/types';
import { nonNegativeInputProps } from '@/components/numericInput';
import type { EditableFieldState } from '@/types/editableField';
import type { FormField } from '@/types/formSchema';
import { computeAutoCalculatedValue } from '@/utils/autoCalculate';
import { formatDateForDisplay } from '@/utils/date';
import { formatComplexValue } from '@/utils/formatValue';

/**
 * Inline component for displaying auto-calculated value pill.
 * Extracted from IIFE to improve readability.
 */
const AutoCalculatedValuePill: React.FC<{ field: FormField; value: string }> = ({
  field,
  value,
}) => {
  const result = useMemo(() => computeAutoCalculatedValue(field, value), [field, value]);

  if (!result || !result.value) return null;

  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md shrink-0">
      <span className="font-medium">{result.label}:</span>
      <span>{result.value}</span>
    </span>
  );
};

// Helper function to get display label for a value
export const getLabel = (value: any, field?: FormField): string => {
  // Handle multi_select arrays - convert to comma-separated labels
  if (field?.type === 'multi_select' && Array.isArray(value) && field.options) {
    if (value.length === 0) return '';
    return value.map(v => field.options?.find(opt => opt.value === v)?.label || v).join(', ');
  }

  // File fields store an asset ID; show a friendly placeholder instead of the raw ID.
  // Full filename + download in the summary view is a follow-up; the edit surface renders the
  // FileField with name/download. Treat the '--'/empty placeholders as "no file".
  if (field?.type === 'file') {
    return value && value !== '--' && value !== '—' ? 'Attached file' : '';
  }

  // If field has options (select/radio), find the matching label
  if (field?.options && field.options.length > 0) {
    const option = field.options.find(opt => opt.value === value);
    if (option) {
      return option.label;
    }
  }

  // For date and dob fields, format the stored value to the MM/DD/YYYY display format
  if ((field?.type === 'date' || field?.type === 'dob') && value !== null && value !== undefined) {
    return formatDateForDisplay(String(value));
  }

  // Handle objects and arrays using shared utility
  if (typeof value === 'object' && value !== null) {
    return formatComplexValue(value);
  }

  // If value is not null/undefined, stringify it
  if (value !== null && value !== undefined) {
    return String(value);
  }

  // Return empty string for null/undefined
  return '';
};

/**
 * Whether a field's AI-extracted value is worth surfacing as a suggestion.
 *
 * True when an extraction exists, belongs to the call being viewed (when one is
 * scoped), renders to something meaningful, and differs from the value the field
 * currently holds. Shared by every surface that annotates an extraction so they
 * cannot drift: the read-only `EditableField` sparkle row, the edit-mode
 * suggestion chip in `ScalarFieldEditable` (BV-1784), and the nested
 * (drug/procedure) leaves in `NestedFieldAccordion` (UAT-1571) — which used to
 * show nothing at all, having no extraction wiring.
 */
export const shouldShowExtractedValue = ({
  extractedValue,
  value,
  field,
  currentCallId,
  sourceCallId,
}: {
  extractedValue: any;
  value: any;
  field?: FormField;
  currentCallId?: string;
  sourceCallId?: string;
}): boolean => {
  if (extractedValue === undefined || extractedValue === null) return false;
  if (currentCallId && sourceCallId && sourceCallId !== currentCallId) return false;
  const extractedLabel = getLabel(extractedValue, field);
  if (extractedLabel === '' || extractedLabel === '--') return false;

  const currentLabel = getLabel(value, field);
  return (
    extractedLabel.toLowerCase() !== currentLabel.toLowerCase() ||
    currentLabel === '' ||
    currentLabel === '--'
  );
};

// ---------------------------------------------------------------------------
// FileFieldValue — read-only display of a file field's stored asset
// ---------------------------------------------------------------------------

/**
 * File fields store only the asset ID. In read-only (review) mode we resolve the real filename via
 * useAsset and display it, mirroring the precedence used by the editable FileField. Falls back to
 * the field label (or "Attachment") while metadata is loading or unavailable.
 */
const FileFieldValue: React.FC<{
  assetId: string;
  caseRecordId?: string;
  fallbackLabel?: string;
  className?: string;
}> = ({ assetId, caseRecordId, fallbackLabel, className }) => {
  const { data: asset } = useAsset(assetId, caseRecordId);
  const fileName = asset?.asset_name || asset?.file_path || fallbackLabel || 'Attachment';

  return <span className={className}>{fileName}</span>;
};

// ---------------------------------------------------------------------------
// EditingControls — input widget selection + save/cancel buttons
// ---------------------------------------------------------------------------

interface EditingControlsProps {
  fieldPath: string;
  field?: FormField;
  editableState: EditableFieldState;
  editedValue: string | boolean | number | string[];
  error?: string;
  isSaving: boolean;
  labelInline: boolean;
}

const EditingControls: React.FC<EditingControlsProps> = ({
  fieldPath,
  field,
  editableState,
  editedValue,
  error,
  isSaving,
  labelInline,
}) => (
  <div className={`flex items-center gap-2 flex-1 ${labelInline ? 'justify-end' : ''}`}>
    <div className={labelInline ? 'max-w-xs' : 'flex-1'}>
      {field?.type === 'multi_select' && field?.options ? (
        <MultiSelect
          options={field.options.map(opt => ({
            label: opt.label,
            value: String(opt.value),
          }))}
          selected={Array.isArray(editedValue) ? editedValue : []}
          onChange={values => {
            editableState.onValueChange(fieldPath, values);
            if (error) editableState.onErrorClear(fieldPath);
          }}
          placeholder={field?.placeholder || 'Select options...'}
          disabled={isSaving}
          className={`h-8 text-sm ${error ? 'border-destructive' : ''}`}
        />
      ) : field?.type === 'select' && field?.options ? (
        <SearchableSelect
          id={field?.id || fieldPath}
          value={Array.isArray(editedValue) ? '' : (editedValue ?? '')}
          onValueChange={value => {
            editableState.onValueChange(fieldPath, value);
            if (error) editableState.onErrorClear(fieldPath);
          }}
          options={field.options}
          placeholder={field?.placeholder || 'Select...'}
          disabled={isSaving}
          error={!!error}
          className="h-8 text-sm"
        />
      ) : (
        <Input
          type={field?.type || 'text'}
          value={
            Array.isArray(editedValue)
              ? editedValue.join(', ')
              : typeof editedValue === 'boolean'
                ? String(editedValue)
                : editedValue
          }
          onChange={e => {
            const raw = e.target.value;
            const next =
              field?.type === 'number' || field?.type === 'float' ? (raw ? Number(raw) : '') : raw;
            editableState.onValueChange(fieldPath, next);
            if (error) editableState.onErrorClear(fieldPath);
          }}
          placeholder={field?.placeholder}
          className={`h-8 text-sm w-full ${error ? 'border-destructive' : ''}`}
          autoFocus
          disabled={isSaving}
          {...(field?.type === 'number' || field?.type === 'float' ? nonNegativeInputProps : {})}
        />
      )}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
    <div className="flex items-center gap-1 ml-2">
      <button
        onClick={() => editableState.onEditSave(fieldPath, field)}
        className={`p-1 rounded text-green-600 ${
          isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-100'
        }`}
        disabled={isSaving}
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        onClick={editableState.onEditCancel}
        className={`p-1 rounded text-destructive ${
          isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-100'
        }`}
        disabled={isSaving}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// ExtractedValueDisplay — AI extraction annotation with accept + review
// ---------------------------------------------------------------------------

export interface ExtractedValueDisplayProps {
  extractedValue: any;
  field?: FormField;
  fieldPath: string;
  label: string;
  isHovered: boolean;
  isHumanAudit: boolean;
  isEditingField: boolean;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  sourceCallId?: string;
  evidence?: FieldEvidence[];
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}

export const ExtractedValueDisplay: React.FC<ExtractedValueDisplayProps> = ({
  extractedValue,
  field,
  fieldPath,
  label,
  isHovered,
  isHumanAudit,
  isEditingField,
  onAcceptExtracted,
  sourceCallId,
  evidence,
  onNavigateToCall,
  onNavigateToAudit,
}) => (
  <div className="mt-1.5">
    {isHumanAudit && isHovered && !isEditingField && onAcceptExtracted ? (
      <div className="flex items-center gap-2 text-xs">
        <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <button
          onClick={() => onAcceptExtracted(fieldPath, extractedValue, label)}
          className="text-muted-foreground hover:underline hover:text-foreground"
        >
          Accept {getLabel(extractedValue, field)}?
        </button>
        {onNavigateToCall && (
          <>
            <ReviewButton
              sourceCallId={sourceCallId}
              evidence={evidence}
              onNavigateToCall={onNavigateToCall}
              onNavigateToAudit={onNavigateToAudit}
            />
            <ReasoningTooltip evidence={evidence} />
          </>
        )}
      </div>
    ) : (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <span>{getLabel(extractedValue, field)}</span>
        {onNavigateToCall && (
          <>
            <ReviewButton
              sourceCallId={sourceCallId}
              evidence={evidence}
              onNavigateToCall={onNavigateToCall}
              onNavigateToAudit={onNavigateToAudit}
              className="text-xs"
            />
            <ReasoningTooltip evidence={evidence} />
          </>
        )}
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// EditableField — orchestrator component
// ---------------------------------------------------------------------------

export interface EditableFieldProps {
  label: string;
  fieldPath: string;
  value: string;
  extractedValue?: any; // AI-extracted value to display alongside main value
  field?: FormField;
  editableState?: EditableFieldState | null;
  className?: {
    container?: string;
    label?: string;
    value?: string;
    editButton?: string;
    nonEditingLayout?: string;
    labelInline?: boolean;
  };
  hasError?: boolean;
  isHumanAudit?: boolean;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  sourceCallId?: string;
  evidence?: FieldEvidence[];
  outputEvidence?: FieldEvidence[];
  /** Present when this field was marked "heard" during a call — renders a dedicated seek icon. */
  outputHeardSeek?: HeardSeek;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
  /** The ID of the call currently being viewed — used to filter sparkles to current-call extractions only */
  currentCallId?: string;
  /** Case ID for File-type fields, so the read-only view can resolve the asset's filename. */
  caseRecordId?: string;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  label,
  fieldPath,
  value,
  extractedValue,
  field,
  editableState = null,
  className = {},
  hasError = false,
  isHumanAudit = false,
  onAcceptExtracted,
  sourceCallId,
  evidence,
  outputEvidence,
  outputHeardSeek,
  onNavigateToCall,
  onNavigateToAudit,
  currentCallId,
  caseRecordId,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isEditable = editableState !== null;
  const isEditingField = editableState?.editingFields[fieldPath] || false;
  const editedValue = editableState?.editedValues[fieldPath] ?? '';
  const error = editableState?.fieldErrors[fieldPath];
  const isSaving = editableState?.isSaving || false;

  // Determine if extracted value should be shown.
  // When currentCallId is set, only show sparkle for extractions from the current call.
  const shouldShowExtracted = useMemo(
    () => shouldShowExtractedValue({ extractedValue, value, field, currentCallId, sourceCallId }),
    [extractedValue, value, field, currentCallId, sourceCallId],
  );

  // Show "Not captured" indicator when extracted_data exists but field is missing/null,
  // while output_data has a value. Shows in all states; human_audit hover shows "Unset value?".
  // extractedValue is null when extractedData was provided but field is missing (vs undefined = no extraction context)
  const showExtractedEmpty = useMemo(() => {
    if (shouldShowExtracted) return false;
    if (extractedValue !== null) return false;
    if (currentCallId && sourceCallId && sourceCallId !== currentCallId) return false;
    const currentLabel = getLabel(value, field);
    return currentLabel !== '' && currentLabel !== '--' && currentLabel !== '—';
  }, [shouldShowExtracted, extractedValue, value, field, currentCallId, sourceCallId]);

  // Merge default classes with provided classes
  const classes = {
    container:
      className.container || 'group px-2 py-2 -mx-2 rounded hover:bg-muted transition-colors',
    label: className.label || 'text-xs text-muted-foreground mb-1',
    value: className.value || 'text-sm font-medium flex-1',
    editButton:
      className.editButton ||
      'p-1 hover:bg-muted rounded text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity',
    nonEditingLayout: className.nonEditingLayout || 'flex items-center gap-2',
    labelInline: className.labelInline ?? false,
  };

  const isRequired = field?.validation?.required;

  const labelEl = (tag: 'div' | 'span') => {
    const cls = `${classes.label} ${hasError ? 'text-destructive' : ''}`;
    const content = (
      <>
        {label}
        {isRequired && <span className="text-destructive ml-1">*</span>}
      </>
    );
    return tag === 'div' ? (
      <div className={cls}>{content}</div>
    ) : (
      <span className={cls}>{content}</span>
    );
  };

  return (
    <div
      className={classes.container}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!classes.labelInline && labelEl('div')}
      <div className={classes.nonEditingLayout}>
        {isEditingField && editableState ? (
          <>
            {classes.labelInline && labelEl('span')}
            <EditingControls
              fieldPath={fieldPath}
              field={field}
              editableState={editableState}
              editedValue={editedValue}
              error={error}
              isSaving={isSaving}
              labelInline={classes.labelInline}
            />
          </>
        ) : (
          <>
            {classes.labelInline && labelEl('span')}
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5">
                {field?.type === 'file' && value && value !== '--' && value !== '—' ? (
                  <FileFieldValue
                    assetId={String(value)}
                    caseRecordId={caseRecordId}
                    fallbackLabel={field.label}
                    className={`text-sm font-medium ${hasError ? 'text-destructive' : ''}`}
                  />
                ) : (
                  <span className={`text-sm font-medium ${hasError ? 'text-destructive' : ''}`}>
                    {getLabel(value, field) || '—'}
                  </span>
                )}
                {!!outputEvidence?.length && onNavigateToCall && (
                  <>
                    <ReviewButton
                      sourceCallId={sourceCallId}
                      evidence={outputEvidence}
                      onNavigateToCall={onNavigateToCall}
                      onNavigateToAudit={onNavigateToAudit}
                      className="text-xs"
                    />
                    <ReasoningTooltip evidence={outputEvidence} />
                  </>
                )}
              </div>

              {shouldShowExtracted && (
                <ExtractedValueDisplay
                  extractedValue={extractedValue}
                  field={field}
                  fieldPath={fieldPath}
                  label={label}
                  isHovered={isHovered}
                  isHumanAudit={isHumanAudit}
                  isEditingField={isEditingField}
                  onAcceptExtracted={onAcceptExtracted}
                  sourceCallId={sourceCallId}
                  evidence={evidence}
                  onNavigateToCall={onNavigateToCall}
                  onNavigateToAudit={onNavigateToAudit}
                />
              )}

              {showExtractedEmpty && (
                <div className="mt-1.5">
                  {isHumanAudit && isHovered && !isEditingField && onAcceptExtracted ? (
                    <div className="flex items-center gap-2 text-xs">
                      <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      <button
                        onClick={() => onAcceptExtracted?.(fieldPath, null, label)}
                        className="text-muted-foreground hover:underline hover:text-foreground"
                      >
                        Unset value?
                      </button>
                      {onNavigateToCall && (
                        <>
                          <ReviewButton
                            sourceCallId={sourceCallId}
                            evidence={evidence}
                            onNavigateToCall={onNavigateToCall}
                            onNavigateToAudit={onNavigateToAudit}
                          />
                          <ReasoningTooltip evidence={evidence} />
                        </>
                      )}
                      {outputHeardSeek?.source_id && onNavigateToCall && (
                        <HeardSeekButton
                          heardSeek={outputHeardSeek}
                          onNavigateToCall={onNavigateToCall}
                          className="text-xs"
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-destructive/70">
                      <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      <span className="italic">Not captured</span>
                      {onNavigateToCall && (
                        <>
                          <ReviewButton
                            sourceCallId={sourceCallId}
                            evidence={evidence}
                            onNavigateToCall={onNavigateToCall}
                            onNavigateToAudit={onNavigateToAudit}
                            className="text-xs"
                          />
                          <ReasoningTooltip evidence={evidence} />
                        </>
                      )}
                      {outputHeardSeek?.source_id && onNavigateToCall && (
                        <HeardSeekButton
                          heardSeek={outputHeardSeek}
                          onNavigateToCall={onNavigateToCall}
                          className="text-xs"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Heard affordance when no extracted row is present to host it inline. */}
              {!showExtractedEmpty && outputHeardSeek?.source_id && onNavigateToCall && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <HeardSeekButton
                    heardSeek={outputHeardSeek}
                    onNavigateToCall={onNavigateToCall}
                    className="text-xs"
                  />
                </div>
              )}

              {field?.autoCalculates && (
                <div className="flex justify-end mt-1">
                  <AutoCalculatedValuePill field={field} value={value} />
                </div>
              )}
            </div>
            {isEditable && !isSaving && editableState && (
              <button
                onClick={() => editableState.onEditStart(fieldPath, value === '--' ? '' : value)}
                className={classes.editButton}
                disabled={isSaving}
              >
                <Edit2 className="w-3 h-3" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
